<script setup lang="ts">
/**
 * BottomSheet Component
 * Mobile-native modal that slides up from the bottom
 * Simple implementation without drag-to-dismiss for reliability
 */

import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from 'primevue/button'

const { t } = useI18n()

interface Props {
  visible: boolean
  title?: string
  height?: 'auto' | 'half' | 'full'
  showHandle?: boolean
  closable?: boolean
  dismissable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  height: 'auto',
  showHandle: true,
  closable: true,
  dismissable: true
})

const emit = defineEmits<{
  'update:visible': [value: boolean]
  close: []
}>()

const sheetRef = ref<HTMLElement | null>(null)
const isAnimating = ref(false)

// Lock body scroll when sheet is open
function lockBodyScroll() {
  document.body.style.overflow = 'hidden'
  document.body.style.touchAction = 'none'
}

function unlockBodyScroll() {
  document.body.style.overflow = ''
  document.body.style.touchAction = ''
}

// Handle close
function handleClose() {
  if (!props.closable) return
  emit('update:visible', false)
  emit('close')
}

// Handle backdrop click
function handleBackdropClick() {
  if (props.dismissable) {
    handleClose()
  }
}

// Handle escape key
function handleEscape(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.visible && props.closable) {
    handleClose()
  }
}

// Focus trap: keep focus within the sheet
function handleFocusTrap(event: KeyboardEvent) {
  if (event.key !== 'Tab' || !sheetRef.value) return

  const focusableElements = sheetRef.value.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )
  const firstElement = focusableElements[0] as HTMLElement
  const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault()
    lastElement?.focus()
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault()
    firstElement?.focus()
  }
}

// Watch visibility changes
watch(() => props.visible, async (isVisible) => {
  if (isVisible) {
    isAnimating.value = true
    lockBodyScroll()
    await nextTick()
    // Focus first focusable element
    const firstFocusable = sheetRef.value?.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ) as HTMLElement
    firstFocusable?.focus()
  } else {
    unlockBodyScroll()
  }
})

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
  document.addEventListener('keydown', handleFocusTrap)

  if (props.visible) {
    lockBodyScroll()
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
  document.removeEventListener('keydown', handleFocusTrap)
  unlockBodyScroll()
})

function handleAnimationEnd() {
  isAnimating.value = false
}
</script>

<template>
  <Teleport to="body">
    <Transition name="sheet">
      <div
        v-if="visible"
        class="bottom-sheet-overlay"
        @click.self="handleBackdropClick"
      >
        <div
          ref="sheetRef"
          class="bottom-sheet"
          :class="[`bottom-sheet--${height}`]"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="title ? 'sheet-title' : undefined"
          @animationend="handleAnimationEnd"
        >
          <!-- Handle bar -->
          <div v-if="showHandle" class="sheet-handle">
            <div class="sheet-handle-bar"></div>
          </div>

          <!-- Header -->
          <div v-if="title || closable" class="sheet-header">
            <h2 v-if="title" id="sheet-title" class="sheet-title">
              {{ title }}
            </h2>
            <Button
              v-if="closable"
              icon="pi pi-times"
              text
              rounded
              class="sheet-close-btn"
              :aria-label="t('common.close')"
              @click="handleClose"
            />
          </div>

          <!-- Content -->
          <div class="sheet-content">
            <slot></slot>
          </div>

          <!-- Footer slot -->
          <div v-if="$slots.footer" class="sheet-footer">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Overlay */
.bottom-sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

/* Sheet container */
.bottom-sheet {
  position: relative;
  width: 100%;
  max-width: 100%;
  background: var(--surface-card, #ffffff);
  border-radius: 24px 24px 0 0;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - var(--safe-area-top) - 24px);
  overflow: hidden;
}

/* Height variants */
.bottom-sheet--auto {
  max-height: calc(90vh - var(--safe-area-top));
}

.bottom-sheet--half {
  height: 50vh;
  max-height: calc(50vh - var(--safe-area-top));
}

.bottom-sheet--full {
  height: calc(100vh - var(--safe-area-top) - 24px);
}

/* Handle bar */
.sheet-handle {
  padding: 12px 0 8px;
  display: flex;
  justify-content: center;
  cursor: grab;
}

.sheet-handle-bar {
  width: 36px;
  height: 4px;
  background: var(--neutral-300, #D4D4D8);
  border-radius: 2px;
}

/* Header */
.sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem 1rem;
  border-bottom: 1px solid var(--neutral-100, #F4F4F5);
}

.sheet-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--neutral-900, #18181B);
  margin: 0;
  flex: 1;
}

.sheet-close-btn {
  width: 36px !important;
  height: 36px !important;
  color: var(--neutral-500, #71717A) !important;
  flex-shrink: 0;
}

.sheet-close-btn:hover {
  background: var(--neutral-100, #F4F4F5) !important;
  color: var(--neutral-700, #3F3F46) !important;
}

/* Content */
.sheet-content {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

/* Footer */
.sheet-footer {
  padding: 1rem;
  padding-bottom: calc(1rem + var(--safe-area-bottom));
  border-top: 1px solid var(--neutral-100, #F4F4F5);
  background: var(--surface-card, #ffffff);
}

/* Transition animations */
.sheet-enter-active {
  animation: sheetSlideUp var(--duration-sheet, 300ms) var(--transition-sheet, cubic-bezier(0.32, 0.72, 0, 1));
}

.sheet-enter-active .bottom-sheet-overlay {
  animation: fadeIn var(--duration-sheet, 300ms) ease-out;
}

.sheet-leave-active {
  animation: sheetSlideDown var(--duration-sheet, 300ms) var(--transition-sheet, cubic-bezier(0.32, 0.72, 0, 1));
}

.sheet-leave-active .bottom-sheet-overlay {
  animation: fadeOut var(--duration-sheet, 300ms) ease-out;
}

@keyframes sheetSlideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes sheetSlideDown {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

/* Desktop: Convert to centered dialog */
@media (min-width: 768px) {
  .bottom-sheet-overlay {
    align-items: center;
    padding: 2rem;
  }

  .bottom-sheet {
    max-width: 500px;
    border-radius: 24px;
    max-height: calc(100vh - 4rem);
  }

  .bottom-sheet--half {
    height: auto;
    max-height: 50vh;
  }

  .bottom-sheet--full {
    height: auto;
    max-height: calc(100vh - 4rem);
  }

  .sheet-handle {
    display: none;
  }

  .sheet-footer {
    padding-bottom: 1rem;
  }
}
</style>
